import { FormHelperText, Grid, Stack, Box, Button, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { listCategoriesAll } from '../../api/products';
import useAuthToken from '../../hooks/getUserData';
import CustomButton from '../elements/CustomButton';
import Dropdown2 from '../elements/Dropdown2';
import InputField from '../elements/InputField';

const extractStoreName = (url) => {
  try {
    const { hostname } = new URL(url);
    const parts = hostname.replace(/^www\./, '').split('.');
    return parts[0].charAt(0) + parts[0].slice(1);
  } catch {
    return '';
  }
};

const ProductForm = ({ onSubmit, product = {}, loading }) => {
  const [categories, setCategories] = useState([]);
  const token = useAuthToken();
  const [showPrimaryImageInput, setShowPrimaryImageInput] = useState(false);
  const [showSecondaryImageInput, setShowSecondaryImageInput] = useState(false);
  const [urlEdited, setUrlEdited] = useState(false);

  useEffect(() => {
    async function fetchCustomerData() {
      try {
        const res = await listCategoriesAll(token);
        if (res.ok) {
          const data = await res.json();
          const categoryData = Array.isArray(data?.categories)
            ? data.categories.map((category) => ({
                value: category.id || '',
                label: category.name || '',
                details: category.category_details || [],
              }))
            : [];

          setCategories(categoryData);
        } else {
          throw new Error('Failed to fetch customers');
        }
      } catch (error) {
        console.error('Error fetching categories list:', error);
      }
    }
    fetchCustomerData();
  }, [token]);

  const provider = (product.providers && product.providers[0]) || null;
  const providerUrl = provider?.url || product?.url || '';
  const providerNameFromProvider = provider?.provider_name || extractStoreName(providerUrl) || '';

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          title: product?.title || '',
          description: product?.description || '',
          category: product?.category || '',
          brand: product?.brand || '',
          productUrl: providerUrl || product?.productUrl || '',
          imageUrl: product?.pictures?.[0] || '',
          status: product?.status || 'active',
          providerId: provider?.id || '',
          providerPrice: provider?.price || product?.price || '',
          providerName: providerNameFromProvider,
          autoSuggestionId: product?.auto_suggestion_id || '',
          primaryImage: '',
          secondaryImage: '',
        }}
        validationSchema={Yup.object().shape({
          title: Yup.string()
            .matches(
              /^[\s\S]{1,255}$/,
              'Product Name should not exceed 255 characters'
            )
            .required('Product Name is required'),
          providerPrice: Yup.string()
            .matches(/^\$?[\d,]+(\.\d{1,2})?$/, 'Invalid price format')
            .required('Product Price is required'),
          description: Yup.string().max(700),
          category: Yup.string().required('Category is required'),
          imageUrl: Yup.string().nullable().url('Invalid URL format'),
          status: Yup.string().required('Status is required'),
          productUrl: Yup.string().required('URL is required').url('Invalid URL format'),
          brand: Yup.string().required('Brand is required'),
        })}
        onSubmit={(values, formikHelpers) => {
          const payload = {
            ...values,
            ...(values.primaryImage ? { primaryImage: values.primaryImage } : {}),
            ...(values.secondaryImage ? { secondaryImage: values.secondaryImage } : {}),
            providerUrl: values.productUrl || providerUrl,
            providerPrice: values.providerPrice,
            providerId: values.providerId,
            autoSuggestionId: values.autoSuggestionId,
          };
          onSubmit(payload, formikHelpers);
        }}>
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputField
                    label="Product ID"
                    id="autoSuggestionId"
                    type="text"
                    value={values.autoSuggestionId || ''}
                    name="autoSuggestionId"
                    fullWidth
                    placeholder="e.g. MD-399"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    disabled={product.auto_suggestion_id ? true : false}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="status"
                    selected={values.status}
                    handleChange={handleChange}
                    elementLabel="Status"
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    required
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Product name"
                    id="title"
                    type="title"
                    value={values.title}
                    name="title"
                    fullWidth
                    placeholder="Product name"
                    error={Boolean(touched.title && errors.title)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="category"
                    selected={values.category}
                    handleChange={(e) => {
                      handleChange(e);
                    }}
                    elementLabel="Category"
                    options={categories}
                    required
                    error={Boolean(touched.category && errors.category)}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputField
                    label="Brand name"
                    id="brand"
                    type="brand"
                    value={values.brand}
                    name="brand"
                    fullWidth
                    placeholder="Brand name"
                    error={Boolean(touched.brand && errors.brand)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Description"
                    id="description"
                    type="text"
                    value={values.description}
                    name="description"
                    fullWidth
                    rows={5}
                    multiline
                    placeholder="Enter product description"
                    error={Boolean(touched.description && errors.description)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Product / Provider URL"
                    id="productUrl"
                    type="text"
                    value={values.productUrl}
                    name="productUrl"
                    fullWidth
                    placeholder="Product URL (used for provider)"
                    error={Boolean(touched.productUrl && errors.productUrl)}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      setUrlEdited(true);
                      const storeName = extractStoreName(e.target.value);
                      setFieldValue('providerName', storeName);
                    }}
                  />
                </Stack>
                {urlEdited && values.providerName && (
                  <FormHelperText sx={{ mt: 0.5 }}>
                    Provider / store: <strong>{values.providerName}</strong>
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Price"
                    id="providerPrice"
                    type="text"
                    value={values.providerPrice ? `$${values.providerPrice}` : ''}
                    name="providerPrice"
                    fullWidth
                    required
                    placeholder="Product Price"
                    error={Boolean(touched.providerPrice && errors.providerPrice)}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const formattedPrice = e.target.value.replace('$', '');
                      setFieldValue('providerPrice', formattedPrice);
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>Product Image</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <img
                      src={values.imageUrl || product?.pictures?.[0] || ''}
                      alt="primary"
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, background: '#f0f0f0', border: '1px solid #ccc' }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowPrimaryImageInput((s) => !s)}
                    >
                      {showPrimaryImageInput ? 'Cancel' : 'Update image'}
                    </Button>
                  </Box>
                  {showPrimaryImageInput && (
                    <InputField
                      label=""
                      id="primaryImage"
                      type="text"
                      value={values.primaryImage}
                      name="primaryImage"
                      fullWidth
                      placeholder="Enter image URL to upload"
                      onBlur={handleBlur}
                      onChange={(e) => setFieldValue('primaryImage', e.target.value)}
                    />
                  )}
                </Stack>
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography variant="caption">Secondary Image</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <img
                      src={product?.pictures?.[1] || ''}
                      alt="secondary"
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, background: '#f0f0f0', border: '1px solid #ccc' }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowSecondaryImageInput((s) => !s)}
                    >
                      {showSecondaryImageInput ? 'Cancel' : 'Update image'}
                    </Button>
                  </Box>
                  {showSecondaryImageInput && (
                    <InputField
                      label="Secondary image URL"
                      id="secondaryImage"
                      type="text"
                      value={values.secondaryImage}
                      name="secondaryImage"
                      fullWidth
                      placeholder="Enter image URL to upload"
                      onBlur={handleBlur}
                      onChange={(e) => setFieldValue('secondaryImage', e.target.value)}
                    />
                  )}
                </Stack>
              </Grid> */}

              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
              <Grid item xs={12}>
                <CustomButton loading={loading} />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ProductForm;
